"use client";

import { useState, useEffect } from "react";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import useApi, { Reserva, ReservaB2C, ReservaB2B } from "../../hooks/useApi";
import B2CReservas from "./B2CReservas";
import B2BReservas from "./B2BReservas";
import AllReservas from "./AllReservas";

export default function Reservas() {
  const [activeTab, setActiveTab] = useState<"all" | "b2c" | "b2b">("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [thirdPartyFilter, setThirdPartyFilter] = useState<boolean | null>(null);
  const { getReservas, createReserva, updateReserva, deleteReserva } = useApi();

  const [reservas, setReservas] = useState<Reserva[]>([]);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await getReservas();
        setReservas(data);
      } catch (error) {
        // Removed console logs
      }
    }
    loadData();
  }, [getReservas]);

  const handleAddReserva = async (
    newReserva: Omit<ReservaB2C, "id"> | Omit<ReservaB2B, "id">
  ) => {
    const createdReserva = await createReserva(newReserva);
    setReservas((prev) => [...prev, createdReserva]);
  };

  const handleEditReserva = async (updatedReserva: Reserva) => {
    const editedReserva = await updateReserva(updatedReserva);
    setReservas((prev) =>
      prev.map((r) => (r.id === editedReserva.id ? editedReserva : r))
    );
  };

  const handleDeleteReserva = async (reservaId: number) => {
    await deleteReserva(reservaId);
    setReservas((prev) => prev.filter((r) => r.id !== reservaId));
  };

  // Se separan las reservas según su tipo
  const b2cReservas = reservas.filter(
    (r): r is ReservaB2C => r.type === "b2c"
  );
  const b2bReservas = reservas.filter(
    (r): r is ReservaB2B => r.type === "b2b"
  );

  // Estados de reserva
  const estadoDisplay = {
    pending_payment: "Pendiente de pago",
    partial_payment: "Pago parcial",
    paid: "Pagada",
    no_show: "No presentado",
    completed: "Completada"
  };

  // Function to reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setDateFilter('');
  };

  // Use effect to reset filters when the component mounts or when the active tab changes
  useEffect(() => {
    resetFilters();
  }, [activeTab]);

  return (
    <div>
      <PageMeta
        title="Gestión de reservas | Destiny Caribbean Tours"
        description="Administración completa de las reservas de excursiones."
      />
      <PageBreadcrumb pageTitle="Reservas" />

      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-2 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-3 lg:px-4">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-black dark:text-white">
                Gestión de reservas
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Administre todas las reservas
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="inline-flex items-center shadow-theme-xs">
                {(["all", "b2c", "b2b"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`
                      ${
                        activeTab === tab
                          ? "bg-brand-500 text-white hover:bg-brand-600"
                          : "text-brand-500 bg-transparent hover:bg-brand-500 hover:text-white dark:text-brand-400 dark:hover:bg-brand-600"
                      }
                      inline-flex items-center gap-2 px-4 py-3 text-sm font-medium 
                      ring-1 ring-inset ring-brand-500 transition first:rounded-l-lg last:rounded-r-lg
                    `}
                  >
                    {tab === "all" ? "Todas" : tab === "b2c" ? "Reservas B2C" : "Reservas B2B"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            {activeTab === "all" && (
              <AllReservas
                reservas={reservas}
                searchTerm={searchTerm}
                dateFilter={dateFilter}
                thirdPartyFilter={thirdPartyFilter}
                onSearchChange={setSearchTerm}
                onDateFilterChange={setDateFilter}
                onThirdPartyFilterChange={setThirdPartyFilter}
                onAddReserva={handleAddReserva}
                onEditReserva={handleEditReserva}
                onDeleteReserva={handleDeleteReserva}
              />
            )}
            {activeTab === "b2c" && (
              <B2CReservas
                reservas={b2cReservas}
                searchTerm={searchTerm}
                dateFilter={dateFilter}
                thirdPartyFilter={thirdPartyFilter}
                onSearchChange={setSearchTerm}
                onDateFilterChange={setDateFilter}
                onThirdPartyFilterChange={setThirdPartyFilter}
                onAddReserva={handleAddReserva}
                onEditReserva={handleEditReserva}
                onDeleteReserva={handleDeleteReserva}
              />
            )}
            {activeTab === "b2b" && (
              <B2BReservas
                reservas={b2bReservas}
                searchTerm={searchTerm}
                dateFilter={dateFilter}
                thirdPartyFilter={thirdPartyFilter}
                onSearchChange={setSearchTerm}
                onDateFilterChange={setDateFilter}
                onThirdPartyFilterChange={setThirdPartyFilter}
                onAddReserva={handleAddReserva}
                onEditReserva={handleEditReserva}
                onDeleteReserva={handleDeleteReserva}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}